import React, { useEffect, useState, useCallback } from 'react';
import { Modal } from 'antd';
import { NavLink, useNavigate } from 'react-router-dom';
import './admin.css';
import { Button } from 'antd';
import { API_BASE, authHeaders } from '../config/api';
import {
  ClockCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  GlobalOutlined,
  BarChartOutlined,
  TeamOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  HeatMapOutlined,
  CrownOutlined,
  UserOutlined,
  FormOutlined,
  VideoCameraOutlined,
  LogoutOutlined,
  BookOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { hasFeatureAccess } from './utils/planRestrictions';

const AdminBOSidebar = ({ onShowGuide }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    try {
      // Clear any auth/session data
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      sessionStorage.clear();
    } catch (e) {
      // no-op
    }
    // Navigate to login and replace history so back won't return
    navigate('/', { replace: true });
  };

  const [collapsed, setCollapsed] = useState(false);
  const [comingSoonVisible, setComingSoonVisible] = useState(false);

  // Get user info from localStorage and fetch avatar from API if needed
  const [user, setUser] = useState(null);
  useEffect(() => {
    const loadUser = async () => {
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          const parsed = JSON.parse(stored);
          setUser(parsed);
          
          // If no avatar_url in localStorage, fetch from API
          if (!parsed.avatar_url) {
            try {
              const res = await fetch(`${API_BASE}/me`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
              if (res.ok) {
                const data = await res.json();
                if (data.avatar_url) {
                  const updated = { ...parsed, avatar_url: data.avatar_url };
                  localStorage.setItem('user', JSON.stringify(updated));
                  setUser(updated);
                }
              }
            } catch {}
          }
        }
      } catch {}
    };
    loadUser();
    
    // Listen for storage changes (when profile is updated)
    const handleStorage = (e) => {
      if (e.key === 'user') loadUser();
    };
    window.addEventListener('storage', handleStorage);
    
    // Also listen for custom event when user updates profile
    const handleUserUpdate = () => loadUser();
    window.addEventListener('userUpdated', handleUserUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  const width = collapsed ? 72 : 240;
  const label = (text) => (collapsed ? null : <span style={{ marginLeft: 10, fontSize: 13 }}>{text}</span>);
  
  // Helper to render premium feature labels
  const premiumLabel = (text, feature) => {
    const hasAccess = hasFeatureAccess(currentPlan, feature);
    if (collapsed) return null;
    return (
      <span style={{ marginLeft: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', fontSize: 13 }}>
        <span>{text}</span>
        {!hasAccess && (
          <CrownOutlined 
            style={{ 
              color: '#722ed1', 
              fontSize: 12, 
              marginLeft: 8,
              opacity: 0.7
            }} 
            title="Enterprise Plan Required"
          />
        )}
      </span>
    );
  };

  // Plan badge state
  const [plan, setPlan] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const apiBase = API_BASE;
  // Load current user's resolved current plan
  const loadPlan = useCallback(async () => {
    try {
      const ts = Date.now();
      const res = await fetch(`${apiBase}/my/current-plan?_t=${ts}`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      const body = await res.json().catch(() => (null));
      if (!res.ok) { 
        setPlan(null); 
        return; 
      }
      if (!body || !body.plan_name) {
        setPlan(null);
        setCurrentPlan(null);
        return;
      }
      setPlan({ name: body.plan_name });
      setCurrentPlan(body);
    } catch (err) {
      console.error('Error loading plan:', err);
      setPlan(null);
      setCurrentPlan(null);
    }
  }, [apiBase]);

  useEffect(() => {
    loadPlan();

    // Listen for plan change events
    const handlePlanChange = () => {
      loadPlan();
    };

    window.addEventListener('planChanged', handlePlanChange);

    return () => {
      window.removeEventListener('planChanged', handlePlanChange);
    };
  }, [loadPlan]);

  return (
    <aside className="abo-sidebar" style={{ width, transition: 'width .2s ease' }}>
      {/* Logo Section */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '8px 0 8px',
        marginBottom: 4,
        borderBottom: '1px solid rgba(148,163,184,0.15)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/dashboard/admin')}>
          <img 
            src="/headerlogo.png" 
            alt="Asuma Logo" 
            style={{ 
              height: collapsed ? '24px' : '28px', 
              width: 'auto',
              transition: 'height 0.2s ease'
            }} 
          />
          {!collapsed && (
            <div style={{ marginLeft: 0 }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '700', 
                color: '#fff',
                lineHeight: 1.2
              }}>Asuma</div>
              <div style={{ 
                fontSize: '9px', 
                fontWeight: '400', 
                color: '#94a3b8',
                fontStyle: 'italic',
                marginTop: -2
              }}>Customer intelligence for growing businesses.</div>
            </div>
          )}
        </div>
        <Button
          type="text"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setCollapsed((c) => !c)}
          size="small"
          style={{ color: '#fff', padding: '4px' }}
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        />
      </div>
      <nav>
        <ul>
          <li>
            <NavLink to="/dashboard/admin" className={({isActive}) => isActive ? 'active' : undefined} title="Dashboard">
              <DashboardOutlined />{label('Dashboard')}
            </NavLink>
          </li>

          <li className="abo-divider" aria-hidden="true" />

          {/* AI Chat */}
          <li>
            <NavLink to="/dashboard/admin/ai-chat" className={({isActive}) => isActive ? 'active' : undefined} title="AI Chat">
              <RobotOutlined />{premiumLabel('AI Chat', 'ai_chat')}
            </NavLink>
          </li>

          {/* API Portal */}
          <li>
            <NavLink to="/dashboard/admin/api-portal" className={({isActive}) => isActive ? 'active' : undefined} title="API Portal">
              <ApiOutlined />{premiumLabel('API Portal', 'api_access')}
            </NavLink>
          </li>

          <li className="abo-divider" aria-hidden="true" />

          {/* Websites & Snippets */}
          <li>
            <NavLink to="/dashboard/admin/websites-snippets" className={({isActive}) => isActive ? 'active' : undefined} title="Websites & Snippets">
              <GlobalOutlined />{label('Websites & Snippets')}
            </NavLink>
          </li>

          <li className="abo-divider" aria-hidden="true" />

          {/* HeatMap */}
          <li>
            <NavLink to="/dashboard/admin/heatmap" className={({isActive}) => isActive ? 'active' : undefined} title="HeatMap">
              <HeatMapOutlined />{premiumLabel('HeatMap', 'heatmaps')}
            </NavLink>
          </li>

          {/* Form Analytics */}
          <li>
            <NavLink to="/dashboard/admin/form-analytics" className={({isActive}) => isActive ? 'active' : undefined} title="Form Analytics">
              <FormOutlined />{label('Form Analytics')}
            </NavLink>
          </li>

          {/* Session Recordings */}
          <li>
            <button
              onClick={() => setComingSoonVisible(true)}
              title="Session Recordings"
              style={{ display: 'flex', alignItems: 'center', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px 12px', borderRadius: 8, color: 'inherit', textAlign: 'left' }}
            >
              <VideoCameraOutlined />{label('Session Recordings')}
            </button>
          </li>

          <li className="abo-divider" aria-hidden="true" />
          {/* Analytics & Reports */}
          <li>
            <NavLink to="/dashboard/admin/analytics" className={({isActive}) => isActive ? 'active' : undefined} title="Analytics & Reports">
              <BarChartOutlined />{label('Analytics & Reports')}
            </NavLink>
          </li>

          {/* Channel Comparison */}
          <li>
            <NavLink to="/dashboard/admin/channel-comparison" className={({isActive}) => isActive ? 'active' : undefined} title="Channel Comparison">
              <BarChartOutlined />{label('Channel Comparison')}
            </NavLink>
          </li>

          <li className="abo-divider" aria-hidden="true" />

          {/* Team & Roles */}
          <li>
            <NavLink to="/dashboard/admin/team/manage" className={({isActive}) => isActive ? 'active' : undefined} title="Team & Roles">
              <TeamOutlined />{label('Team & Roles')}
            </NavLink>
          </li>

          <li className="abo-divider" aria-hidden="true" />
          {/* Account Settings */}
          <li>
            <NavLink to="/dashboard/admin/settings" className={({isActive}) => isActive ? 'active' : undefined} title="Account Settings">
              <ProfileOutlined />{label('Account Settings')}
            </NavLink>
          </li>

          <li className="abo-divider" aria-hidden="true" />
          {/* Support */}
          <li>
            <NavLink to="/dashboard/admin/support" className={({isActive}) => isActive ? 'active' : undefined} title="Support">
              <QuestionCircleOutlined />{label('Support')}
            </NavLink>
          </li>

          <li className="abo-divider" aria-hidden="true" />

          {/* Guide Button */}
          <li>
            <button onClick={onShowGuide} title="Show App Guide">
              <BookOutlined />{label('Guide')}
            </button>
          </li>

          <li className="abo-divider" aria-hidden="true" />
          {/* Logout */}
          <li>
            <button onClick={handleLogout} title="Logout">
              <LogoutOutlined />{label('Logout')}
            </button>
          </li>
        </ul>
      </nav>

      <Modal
        open={comingSoonVisible}
        onCancel={() => setComingSoonVisible(false)}
        footer={null}
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '24px 16px' }}>
          <ClockCircleOutlined style={{ fontSize: 48, color: '#6366f1', marginBottom: 16 }} />
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Coming Soon</h2>
          <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.6 }}>
            Session Recordings is under development and will be available soon. Stay tuned!
          </p>
        </div>
      </Modal>

      {/* User Profile Section */}
      {!collapsed && user && (
        <div style={{ padding: '12px 8px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ 
              width: 36, 
              height: 36, 
              borderRadius: '50%', 
              background: '#1890ff', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0
            }}>
              {user.avatar_url ? (
                <img src={`${API_BASE}${user.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <UserOutlined style={{ color: '#fff', fontSize: 18 }} />
              )}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.name || user.email?.split('@')[0] || 'User'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.email || ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan badge */}
      <div style={{ paddingLeft: collapsed ? 0 : 8, paddingRight: collapsed ? 0 : 8, paddingTop: 8 }}>
        {!collapsed && plan && (
          <div style={{ background: '#f0f9ff', border: '1px solid #e0f2fe', borderRadius: 6, padding: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: '#0369a1', marginBottom: 2 }}>Current Plan</div>
            <div style={{ fontWeight: 600, color: '#0c4a6e', fontSize: 12 }}>{plan.name}</div>
          </div>
        )}
        {!collapsed && !plan && (
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, padding: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: '#92400e', marginBottom: 2 }}>No Active Plan</div>
            <div style={{ fontWeight: 600, color: '#78350f', fontSize: 11 }}>Subscribe to get started</div>
          </div>
        )}
      </div>

      {/* Version & Copyright */}
      {!collapsed && (
        <div style={{ padding: '8px 8px 10px', borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 4 }}>
          <div style={{ fontSize: 10, color: 'rgba(148,163,184,0.7)', textAlign: 'center', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, letterSpacing: '0.04em' }}>v2.4.15</div>
            <div>© {new Date().getFullYear()} Asuma Analytics</div>
            <div style={{ fontSize: 9, opacity: 0.7 }}>All rights reserved</div>
          </div>
        </div>
      )}
      {collapsed && (
        <div style={{ padding: '8px 0', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 4 }}>
          <div style={{ fontSize: 9, color: 'rgba(148,163,184,0.6)' }}>v2.4.15</div>
        </div>
      )}

    </aside>
  );
};

export default AdminBOSidebar;
